'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { User } from '@/payload-types'
import type {
  AuthContext,
  Disable2FA,
  ForgotPassword,
  Login,
  Logout,
  ResetPassword,
  Setup2FA,
  Verify2FA,
} from './auth/types'
import { isUserFacingError, notifyError } from '@/app/(frontend)/helpers/error-handler'
import { getWithErrorHandling, postWithErrorHandling } from '@/app/(frontend)/helpers/fetch-helpers'
import { buildTraceparent } from '@/lib/trace-context'
import posthog from 'posthog-js'

const Context = createContext({} as AuthContext)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<null | User>()

  const login = useCallback<Login>(async (args) => {
    try {
      // Use fetch directly to handle 428 status as success case
      // Inject trace headers so backend logs share the same traceId as PostHog events
      const { traceparent, traceId, spanId } = buildTraceparent()
      const posthogSessionId: string = (posthog as any).get_session_id?.() ?? ''
      try {
        posthog.capture('$api_request', {
          traceId,
          spanId,
          url: '/api/users/login',
          method: 'POST',
        })
      } catch {
        /* non-fatal */
      }
      const response = await fetch('/api/users/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          traceparent,
          ...(posthogSessionId ? { 'x-posthog-session-id': posthogSessionId } : {}),
        },
        body: JSON.stringify({
          email: args.email,
          password: args.password,
          ...(args.totpCode && { totpCode: args.totpCode }),
          // Turnstile token is verified server-side in the Users `beforeOperation` login hook.
          // Sent only on the first (password) factor; the 2FA step reuses the human
          // already proven at step 1.
          ...(args.turnstileToken && { turnstileToken: args.turnstileToken }),
        }),
      })

      const result = await response.json()

      // Handle successful login
      if (response.ok) {
        setUser(result.user)
        return result.user
      }

      // Handle 2FA required case (428 status) as a legitimate response, not an error
      if (
        response.status === 428 &&
        (result.errors?.[0]?.data?.requires2FA ||
          result.errors?.[0]?.message === 'נדרש אימות דו-שלבי')
      ) {
        // This is a legitimate "2FA required" response, not an error
        const twoFactorError = new Error('נדרש אימות דו-שלבי') as any
        twoFactorError.requires2FA = true
        twoFactorError.details = result
        throw twoFactorError
      }

      // Handle actual errors
      const errorMessage = result.errors?.[0]?.message || result.error || 'שגיאה בהתחברות למערכת'

      const loginError = new Error(errorMessage) as any
      // Include HTTP status so isUserFacingError can detect user-facing statuses (e.g., 403)
      loginError.details = { ...result, status: response.status }
      throw loginError
    } catch (error: any) {
      // If it's our 2FA required "error", don't show notification - just rethrow
      if (error.requires2FA) {
        throw error
      }

      // For network errors or other unexpected errors
      if (!error.details) {
        notifyError({ message: 'שגיאה בהתחברות למערכת' })
      }

      // Always throw the error so the caller can handle it
      throw error
    }
  }, [])
  const logout = useCallback<Logout>(async () => {
    // Clear the user BEFORE awaiting the logout request so that any provider
    // listening on `user` (CompaniesProvider, UserNotificationComponent,
    // ImpersonationProvider, ...) can abort in-flight fetches BEFORE the
    // server-side cookie is invalidated. Otherwise we hit a race: cookie is
    // cleared by /api/users/logout, but in-flight requests started moments
    // before still hit the server and come back as 401 — which then surface
    // in the error dialog on /auth/signin (see logout 401 race report).
    setUser(null)
    try {
      await postWithErrorHandling(
        '/api/users/logout',
        {},
        {
          headers: {
            Authorization: '',
            'Content-Type': 'application/json',
          },
          errorMessage: 'שגיאה בהתנתקות מהמערכת',
          notifyOnError: false, // We'll handle notification ourselves
        },
      )
    } catch (error) {
      const isUserFacing: boolean = isUserFacingError(error)
      if (isUserFacing) {
        throw error
      }
    }
  }, [])

  // On mount, get user and set
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const user = await getWithErrorHandling('/api/users/me', {
          notifyOnError: false, // Don't notify on error, just set user to null
        })

        if (user) {
          setUser(user.user)
        } else {
          setUser(null)
        }
      } catch (error: any) {
        setUser(null)
      }
    }

    void fetchMe()
  }, [])

  const forgotPassword = useCallback<ForgotPassword>(async (args) => {
    try {
      await postWithErrorHandling('/api/users/forgot-password', args, {
        errorMessage: 'שגיאה בשליחת בקשה לאיפוס סיסמה',
        notifyOnError: false, // We'll handle notification ourselves
      })

      return true
    } catch (error) {
      throw error
    }
  }, [])

  const resetPassword = useCallback<ResetPassword>(async (args) => {
    try {
      // Use fetch directly to handle 428 status as success case
      const response = await fetch('/api/users/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args),
      })

      const result = await response.json()

      // Handle successful reset — do NOT set user in state.
      // The caller will redirect to /auth/signin so the user must log in manually.
      if (response.ok) {
        return result.user
      }

      // Handle 2FA required case (428 status)
      if (
        response.status === 428 &&
        (result.errors?.[0]?.data?.requires2FA ||
          result.errors?.[0]?.message === 'נדרש אימות דו-שלבי')
      ) {
        const twoFactorError = new Error('נדרש אימות דו-שלבי') as any
        twoFactorError.requires2FA = true
        twoFactorError.details = result
        throw twoFactorError
      }

      // Handle actual errors
      // Translate Payload's default "Token is either invalid or has expired"
      // to a user-friendly Hebrew message (issue #3132).
      const rawMessage = result.errors?.[0]?.message || result.error
      const isExpiredToken = /token.*(invalid|expired)/i.test(rawMessage ?? '')
      const errorMessage = isExpiredToken
        ? "קישור איפוס הסיסמה פג תוקף. אנא בקש קישור חדש דרך 'שכחתי סיסמה'."
        : rawMessage || 'שגיאה באיפוס סיסמה'
      const resetError = new Error(errorMessage) as any
      resetError.details = { ...result, status: response.status }
      throw resetError
    } catch (error: any) {
      // If it's our 2FA required "error", don't show notification - just rethrow
      if (error.requires2FA) {
        throw error
      }

      const isUserFacing = isUserFacingError(error)
      if (isUserFacing) {
        throw error
      }

      // For network errors or other unexpected errors
      if (!error.details) {
        notifyError({ message: 'שגיאה באיפוס סיסמה' })
      }
      throw error
    }
  }, [])

  // Verify 2FA code
  const verify2FA = useCallback<Verify2FA>(async (args) => {
    try {
      const result = await postWithErrorHandling(
        '/api/users/2fa-verify',
        {
          token: args.token,
          enableAfterVerification: args.enableAfterVerification,
        },
        {
          errorMessage: 'שגיאה באימות דו-שלבי',
          notifyOnError: false,
        },
      )

      return result.success
    } catch (error) {
      const isUserFacing = isUserFacingError(error)
      if (isUserFacing) {
        throw error
      }
    }
  }, [])

  // Setup 2FA
  const setup2FA = useCallback<Setup2FA>(async () => {
    try {
      const result = await postWithErrorHandling(
        '/api/users/2fa-setup',
        {},
        {
          errorMessage: 'שגיאה בהגדרת אימות דו-שלבי',
          notifyOnError: false,
        },
      )

      return {
        qrCode: result.qrCode,
        manualEntryKey: result.manualEntryKey,
      }
    } catch (error: any) {
      throw error
    }
  }, [])

  // Disable 2FA
  const disable2FA = useCallback<Disable2FA>(async (args) => {
    try {
      const result = await postWithErrorHandling('/api/users/2fa-disable', args, {
        errorMessage: 'שגיאה בביטול אימות דו-שלבי',
        notifyOnError: false,
      })

      // Refresh user data
      const updatedUser = await getWithErrorHandling('/api/users/me', {
        notifyOnError: false,
      })
      if (updatedUser) {
        setUser(updatedUser.user)
      }

      return result.success
    } catch (error) {
      const isUserFacing = isUserFacingError(error)
      if (isUserFacing) {
        throw error
      }
    }
  }, [])

  return (
    <Context.Provider
      value={{
        user,
        setUser,
        login,
        verify2FA,
        setup2FA,
        disable2FA,
        logout,
        forgotPassword,
        resetPassword,
        create: login,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useAuth = () => useContext(Context)
